/**
 * Upload Status Checker Utility
 *
 * Provides functions to check upload status for media files and wait for pending uploads
 * Used to ensure all media URLs are valid HTTPS URLs before rendering
 */

import { getUserId } from "./user-id";
import { getUserMediaItems, UserMediaItem } from "./indexdb";
import { Overlay } from "../../types";

export interface UploadProgress {
  total: number;
  completed: number;
  pending: number;
  failed: number;
}

export interface UploadStatus {
  hasPendingUploads: boolean;
  pendingItems: UserMediaItem[];
  failedItems: UserMediaItem[];
  progress: UploadProgress;
}

/**
 * Check upload status for all overlays with blob URLs
 *
 * @param overlays - Array of overlays to check
 * @returns Upload status information
 */
export const getUploadStatusForOverlays = async (
  overlays: Overlay[]
): Promise<UploadStatus> => {
  const userId = getUserId();
  const allMedia = await getUserMediaItems(userId);

  const pendingItems: UserMediaItem[] = [];
  const failedItems: UserMediaItem[] = [];
  let total = 0;

  // Check each overlay with a src field
  for (const overlay of overlays) {
    if (!("src" in overlay) || !overlay.src) continue;

    // Only check blob URLs
    if (!overlay.src.startsWith("blob:")) continue;

    // Find matching media item
    const mediaItem = allMedia.find((item) => item.serverPath === overlay.src);
    if (!mediaItem) continue;

    total++;

    // Check upload status
    if (
      mediaItem.uploadStatus === "pending" ||
      mediaItem.uploadStatus === "uploading"
    ) {
      pendingItems.push(mediaItem);
    } else if (mediaItem.uploadStatus === "failed") {
      failedItems.push(mediaItem);
    }
  }

  return {
    hasPendingUploads: pendingItems.length > 0,
    pendingItems,
    failedItems,
    progress: {
      total,
      completed: total - pendingItems.length - failedItems.length,
      pending: pendingItems.length,
      failed: failedItems.length,
    },
  };
};

/**
 * Quick check if any overlays have pending uploads
 *
 * @param overlays - Array of overlays to check
 * @returns True if any uploads are pending
 */
export const hasAnyPendingUploads = async (
  overlays: Overlay[]
): Promise<boolean> => {
  const status = await getUploadStatusForOverlays(overlays);
  return status.hasPendingUploads;
};

/**
 * Wait for pending uploads to complete
 *
 * @param overlays - Array of overlays to wait for
 * @param onProgress - Optional callback for progress updates
 * @param timeout - Timeout in milliseconds (default: 30000)
 * @returns Promise that resolves when all uploads complete or timeout
 */
export const waitForPendingUploads = async (
  overlays: Overlay[],
  onProgress?: (progress: UploadProgress) => void,
  timeout: number = 30000
): Promise<void> => {
  const startTime = Date.now();
  const pollInterval = 500; // Check every 500ms

  console.log("[UploadChecker] Waiting for pending uploads to complete...");

  while (Date.now() - startTime < timeout) {
    const status = await getUploadStatusForOverlays(overlays);

    // Call progress callback if provided
    if (onProgress) {
      onProgress(status.progress);
    }

    console.log(
      `[UploadChecker] Upload progress: ${status.progress.completed}/${status.progress.total} complete, ${status.progress.pending} pending, ${status.progress.failed} failed`
    );

    // Check if any uploads failed
    if (status.failedItems.length > 0) {
      const failedNames = status.failedItems.map((item) => item.name).join(", ");
      throw new Error(
        `Upload failed for ${status.failedItems.length} file(s): ${failedNames}. Please try uploading again.`
      );
    }

    // Check if all uploads completed
    if (!status.hasPendingUploads) {
      console.log("[UploadChecker] All uploads completed successfully");
      return;
    }

    // Wait before next check
    await new Promise((resolve) => setTimeout(resolve, pollInterval));
  }

  // Timeout reached
  const status = await getUploadStatusForOverlays(overlays);
  throw new Error(
    `Upload timeout: ${status.progress.pending} file(s) still pending after ${timeout / 1000} seconds. Please try again.`
  );
};

/**
 * Get names of files with pending uploads
 *
 * @param overlays - Array of overlays to check
 * @returns Array of file names with pending uploads
 */
export const getPendingUploadNames = async (
  overlays: Overlay[]
): Promise<string[]> => {
  const status = await getUploadStatusForOverlays(overlays);
  return status.pendingItems.map((item) => item.name);
};
